import React, { useEffect, useState } from "react";
import "./QuickPricerListView.scss";
import { useDispatch, useSelector } from "react-redux";
import { QUICK_PRICER_FORM_CONFIG } from "../quickPricer.form.config";
import FormField from "../../dynamic-form/FormField";
import RenderField from "./RenderField";
import { generateFieldId } from "../../../utils/formId";
import { setFormData } from "../../../features/quickPricer/quickPricerFormSlice";
import { buildQuickPricerPayload } from "../utils/buildQuickPricerPayload";
import { LENDER_VS_BORROW } from "../../../utils/quickPricerConstants";
import { formatCurrency } from "../../../utils/format-currency";
import { formatPricingDecimal } from "../../../utils/quickPricerPricingFormat";
import CustomCheckbox from "../../custom-checkbox";

import TemporaryDrawer from "../../../components/temporary-drawer";

const MAX_PRINT_SELECTION = 3;

const QuickPricerListView = ({
	products,
	ineligibleProducts,
	runQuickPricerFlow,
	handleProductClick,
	onPrintSelectionChange,
	onRateSelectionChange,
	isCorrOrNdcChannel,
}) => {
	const [activeTab, setActiveTab] = useState("eligible");
	const [expandedIds, setExpandedIds] = useState([]);
	const [selectedRows, setSelectedRows] = useState([]);
	const [selectedProgramIds, setSelectedProgramIds] = useState([]);
	const [showSidebar, setShowSidebar] = useState(true);
	const dispatch = useDispatch();

	const reduxFilters = useSelector((state) => state.quickPricerForm.formData);

	const [formState, setFormState] = useState(reduxFilters || {});
	const [errors, setErrors] = useState({});
	const [submitted, setSubmitted] = useState(false);
	const [drawerType, setDrawerType] = useState("");
	const [drawerOpen, setDrawerOpen] = useState({});

	const handleOpenDrawer = () => {
		setDrawerOpen({
			right: true,
		});
	};

	const handleCloseDrawer = () => {
		setDrawerOpen({
			right: false,
		});
	};
	const updateField = (name) => (e) => {
		const value = e?.target?.value ?? e;

		setFormState((prev) => {
			const nextState = { ...prev, [name]: value };

			if (
				name === "buydown" &&
				(value === "None" ||
					prev.buydown === "None" ||
					prev.buydownContributorType === "NotApplicable")
			) {
				return {
					...nextState,
					buydownContributorType:
						value === "None" ? "NotApplicable" : "",
				};
			}

			return nextState;
		});

		setErrors((prev) => ({
			...prev,
			[name]: undefined,
		}));
	};
	const [drawerData, setDrawerData] = useState({});
	const toggleDrawer = (event, drawerType, rowData, productId) => {
		if (drawerType !== undefined && drawerType !== "") {
			console.log("eligible products:", products);
			console.log("expanded product:", productId);

			const parentProductDetails = products?.filter(
  				  (product) => product?.productId === productId
			) || [];

			console.log("parent product details:", parentProductDetails);
			setDrawerType(drawerType);
			setDrawerData({ 
				productDetails:  parentProductDetails[0].productDetails,
				parentProductDetails: parentProductDetails[0],
				rowData: rowData
			});
			handleOpenDrawer();
		} else {
			setDrawerType("");
			handleCloseDrawer();
		}
	};
	const handleShowMoreClick = (e, rowData, productId) => {
		console.log("40 show more:", e, rowData);

		toggleDrawer(e, "quick-pricer-adjusments-detail", rowData,productId);
	};
	const renderLabel = (field) => {
		const isRequired =
			typeof field.required === "function"
				? field.required(formState)
				: field.required;

		return (
			<>
				{field.label}
				{isRequired && (
					<span style={{ color: "red", marginLeft: "2px" }}>*</span>
				)}
			</>
		);
	};

	const validateFilters = () => {
		const sections = QUICK_PRICER_FORM_CONFIG?.sections || [];

		const productSection = sections.find(
			(section) => section?.name === "productPricingSearchFilters"
		);

		const fields = productSection?.fields || [];

		const allowedFieldNames = [
			"interestOnly",
			"buydown",
			"buydownContributorType",
			"amortizationTypes",
			"ARMFixedTerms",
			"desiredRate",
			"desiredPrice",
			"desiredLockPeriod",
			"lenderPaidvsBorrowerPaid",
			"prePaymentPenalty",
		];

		const filteredFields = fields.filter(
			(field) =>
				allowedFieldNames.includes(field?.name) &&
				(!field.visibleIf || field.visibleIf(formState))
		);

		const newErrors = {};

		filteredFields.forEach((field) => {
			const isRequired =
				typeof field.required === "function"
					? field.required(formState)
					: field.required;

			if (!isRequired) return;

			const value = formState[field.name];

			const isEmpty =
				value === undefined ||
				value === null ||
				value === "" ||
				(Array.isArray(value) && value.length === 0);

			if (isEmpty) {
				newErrors[field.name] = `${field.label} is required`;
			}
		});

		return newErrors;
	};

	const handleRefresh = () => {
		setSubmitted(true);
		products = []
		ineligibleProducts = []
		setExpandedIds([]);
	    setSelectedRows([]);
		setSelectedProgramIds([]);

		const validationErrors = validateFilters();

		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}

		setErrors({});

		const updatedFilters = {
			...reduxFilters,
			...formState,
		};

		dispatch(setFormData(updatedFilters));

		const requestBody = buildQuickPricerPayload(updatedFilters);

		runQuickPricerFlow(requestBody);
	};

	const handleTabChange = (tab) => {
		setActiveTab(tab);
		setExpandedIds([]);
		setSelectedRows([]);
		setSelectedProgramIds([]);
	};

	const toggleExpand = (id) => {
		setExpandedIds((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};

	// Keyed by `${productId}_${index}`, not the bare rate index - quotes are
	// re-indexed from 0 within every loan program's own filtered quote list, so
	// a bare index would mark row 0 checked across every expanded program at
	// once instead of just the one actually clicked.
	const toggleCheckbox = (productId, index) => {
		const key = `${productId}_${index}`;

		setSelectedRows((prev) =>
			prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
		);
	};

	// LC-324: selection for Print to PDF, at the loan program level, capped at
	// MAX_PRINT_SELECTION - separate from the per-rate selectedRows above.
	const toggleProgramSelection = (productId) => {
		setSelectedProgramIds((prev) => {
			if (prev.includes(productId)) {
				return prev.filter((id) => id !== productId);
			}
			if (prev.length >= MAX_PRINT_SELECTION) {
				return prev;
			}
			return [...prev, productId];
		});
	};

	useEffect(() => {
		onPrintSelectionChange?.(selectedProgramIds);
	}, [selectedProgramIds, onPrintSelectionChange]);

	useEffect(() => {
		onRateSelectionChange?.(selectedRows);
	}, [selectedRows, onRateSelectionChange]);

	const loanPrograms = activeTab === "eligible" ? products : ineligibleProducts;

	return (
		<div className="page-layout">
			{/* SIDEBAR */}

			<div className={`filter-sidebar ${!showSidebar ? "collapsed" : ""}`}>
				<button className="refresh-btn" onClick={handleRefresh}>
					Refresh Search
				</button>

				<div className="filters-title">Product Search Filters:</div>

				{(() => {
					const sections = QUICK_PRICER_FORM_CONFIG?.sections || [];

					const productSections = sections.filter(
						(section) =>
							section?.name === "productPricingSearchFilters" ||
							section?.name === "loanInformation"
					);

					const fields = productSections.flatMap(
						(section) => section?.fields || []
					);

					const allowedFieldNames = [
						"interestOnly",
						"buydown",
						"buydownContributorType",
						"amortizationTypes",
						"ARMFixedTerms",
						"loanTerms",
						"desiredRate",
						"desiredPrice",
						"desiredLockPeriod",
						"lenderPaidvsBorrowerPaid",
						"prePaymentPenalty",
					];

					// Hidden, not cleared: reduxFilters.lenderPaidvsBorrowerPaid still
					// carries the "YesLenderPaid" default set in index.jsx, so
					// Refresh Search's payload (below) is unaffected by hiding this
					// field here.
					const filteredFields = fields
						.filter((field) => allowedFieldNames.includes(field?.name))
						.filter(
							(field) => !field.visibleIf || field.visibleIf(formState)
						)
						.filter(
							(field) =>
								!(field.name === "lenderPaidvsBorrowerPaid" && isCorrOrNdcChannel)
						);

					return filteredFields.map((field) => {
						if (!field?.name) return null;

						return (
							<FormField
								key={field.name}
								label={renderLabel(field)}
								isFullWidth
							>
								<RenderField
									field={field}
									fieldId={generateFieldId(
										"quick-pricer",
										field.section || "productPricingSearchFilters",
										field.name,
										field?.component || "input"
									)}
									formState={formState}
									updateField={updateField}
									errors={errors}
									setErrors={setErrors}
									submitted={submitted}
								/>
							</FormField>
						);
					});
				})()}
			</div>

			{/* MAIN CONTENT */}

			<div className="main-content">
				<div className="wrapper">
					<div className="tabs">
						<button
							className="toggle-sidebar-btn"
							onClick={() => setShowSidebar(!showSidebar)}
						>
							{showSidebar ? "Hide Filters ◀" : "Show Filters ▶"}
						</button>

						<div
							className={`tab ${activeTab === "eligible" ? "active" : ""}`}
							onClick={() => handleTabChange("eligible")}
						>
							Eligible Pricing Results ({products
							.filter(
								(loan) =>
									reduxFilters?.desiredLockPeriod == loan.lockPeriod
							).length})
						</div>

						<div
							className={`tab ${activeTab === "ineligible" ? "active" : ""}`}
							onClick={() => handleTabChange("ineligible")}
						>
							Ineligible Pricing Results ({ineligibleProducts?.length})
						</div>

						{!isCorrOrNdcChannel && (
							<div className="scenario-fields">
								<div className="scenario-field">
									<label>LPC vs BPC</label>
									<input
										type="text"
										value={
											LENDER_VS_BORROW.find(
												(i) =>
													i.value === reduxFilters["lenderPaidvsBorrowerPaid"]
											)?.label || ""
										}
										disabled
									/>
								</div>
								{reduxFilters["lenderPaidvsBorrowerPaid"] === "YesLenderPaid" &&
									products?.length > 0 &&
									products[0]?.loCompensationPercent != null && (
										<div className="scenario-field">
											<label>LPC Percentage</label>
											<input
												type="text"
												value={
													products[0]?.loCompensationPercent
														? `${formatPricingDecimal(products[0]?.loCompensationPercent)}%`
														: "%"
												}
												disabled
											/>
										</div>
									)}
								{reduxFilters["lenderPaidvsBorrowerPaid"] === "YesLenderPaid" &&
									products?.length > 0 &&
									products[0]?.loCompensationDollars > 0 &&
									products[0]?.loCompensationDollars != null && (
										<div className="scenario-field">
											<label>Max Compensation</label>
											<input
												type="text"
												value={formatCurrency.format(
													products[0]?.loCompensationDollars
												)}
												disabled
											/>
										</div>
									)}
							</div>
						)}
					</div>

					{/* RESULTS SCROLL AREA */}

					<div className="results-container">
						{activeTab === "eligible" && loanPrograms.length === 0 && (
							<div className="no-data">No eligible data</div>
						)}

						{activeTab !== "eligible" && loanPrograms.length === 0 && (
							<div className="no-data">No Ineligible data</div>
						)}

						{activeTab === "eligible" && loanPrograms.length > 0 && (
							<div className="loan-header-grid">
								<div></div>
								<div>Loan Program</div>
								<div>Desired Rate</div>
								<div>Desired Price</div>
								<div>Lock Period</div>
								<div></div>
							</div>
						)}

						{activeTab === "ineligible" && loanPrograms.length > 0 && (
							<div className="ineligible-loan-header-grid">
								<div>Loan Program</div>
								<div>Reason</div>
								<div></div>
							</div>
						)}

						{activeTab === "eligible" &&
							loanPrograms.length > 0 &&
							loanPrograms
							.filter(
								(loan) =>
									reduxFilters?.desiredLockPeriod == loan.lockPeriod
							)
							.map((loan) => (
								<div key={loan.productId} className="loan-group">
									<div className="loan-summary">
										<div className="print-select">
											<CustomCheckbox
												checked={selectedProgramIds.includes(loan.productId)}
												handleChange={() =>
													toggleProgramSelection(loan.productId)
												}
												disabled={
													!selectedProgramIds.includes(loan.productId) &&
													selectedProgramIds.length >= MAX_PRINT_SELECTION
												}
												inputProps={{
													"aria-label": `Select ${loan.productName} for print`,
												}}
											/>
										</div>
										<div>{loan.productName}</div>
										<div>{formatPricingDecimal(loan.rate)}</div>
										<div>{formatPricingDecimal(loan.price)}</div>
										<div>{loan.lockPeriod}</div>

										<div
											className="expand"
											onClick={() => {
												toggleExpand(loan.productId);

												if (!loan.productDetails) {
													handleProductClick(loan.productId);
												}
											}}
										>
											{expandedIds.includes(loan.productId)
												? "Show Less ▲"
												: "Show All Options ▼"}
										</div>
									</div>

									<div
										className={
											expandedIds.includes(loan.productId)
												? "expand-wrapper open"
												: "expand-wrapper"
										}
									>
										<div className="pricing-table-container">
											<table className="pricing-table">
												<thead>
													<tr>
														<th></th>
														<th>Rate</th>
														<th>Price</th>
														<th>Points (%)</th>
														<th>Points ($)</th>
														<th>Payment</th>
														<th>Lock</th>
														<th>Action</th>
													</tr>
												</thead>

												<tbody>
													{loan.productDetails?.data.quotes
														?.filter((quote) => loan.lockPeriod === quote.lockPeriod)
														.map((quote, index) => (
															<tr key={index}>
															<td>
																<input
																type="checkbox"
																className="rate-select-checkbox"
																checked={selectedRows.includes(
																	`${loan.productId}_${index}`
																)}
																onChange={() =>
																	toggleCheckbox(loan.productId, index)
																}
																/>
															</td>

															<td>{formatPricingDecimal(quote.rate)}</td>
															<td>{formatPricingDecimal(quote.price)}</td>
															<td>{formatPricingDecimal(quote.discountPercent)}</td>
															<td>{formatPricingDecimal(quote.discountDollar)}</td>
															<td>{formatPricingDecimal(quote.totalPayment)}</td>
															<td>{quote.lockPeriod}</td>
															<td
																className="view-details"
																onClick={(e) =>
																handleShowMoreClick(e, quote, loan.productId)
																}
															>
																View Details
															</td>
															</tr>
													))}
												</tbody>
											</table>
										</div>
									</div>
								</div>))}

						{activeTab === "ineligible" &&
							loanPrograms.length > 0 &&
							loanPrograms.map((loan) => (
								<div key={loan.productId} className="loan-group">
									<div className="ineligible-loan-summary">
										<div>{loan.productName}</div>

										<div>
											{loan?.ineligibleReason?.map((reason, index) => (
												<span key={index}>
													{reason}
													{index < loan.ineligibleReason.length - 1 && ", "}
													<br />
												</span>
											))}
										</div>
									</div>
								</div>
							))}

						<TemporaryDrawer
							// drawer props
							drawerOpen={drawerOpen}
							handleCloseDrawer={handleCloseDrawer}
							drawerDirection="right"
							drawerType={drawerType}
							toggleDrawer={toggleDrawer}
							drawerData={drawerData}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default QuickPricerListView;
